import { useEffect, useState } from 'react';
import type { Vendedor } from '@/services/store.service';
import { fotoVendedorUrl } from '@/services/api-adapter';

interface SelectorVendedorProps {
  abierto: boolean;
  vendedores: Vendedor[];
  actual: Vendedor | null;
  onElegir: (v: Vendedor | null) => void;
  onCerrar: () => void;
}

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

/** Foto del vendedor; sin foto (o si no carga), sus iniciales. */
function Avatar({ vendedor }: { vendedor: Vendedor }) {
  const [fallo, setFallo] = useState(false);
  const url = fotoVendedorUrl(vendedor);
  if (url && !fallo) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        onError={() => setFallo(true)}
        className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-stone-100"
      />
    );
  }
  return (
    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1e2d4a] text-white text-sm font-bold flex-shrink-0">
      {iniciales(vendedor.nombre)}
    </span>
  );
}

/** Ventana para que el cliente elija con qué vendedor hablar por WhatsApp. */
export default function SelectorVendedor({
  abierto,
  vendedores,
  actual,
  onElegir,
  onCerrar,
}: SelectorVendedorProps) {
  useEffect(() => {
    if (!abierto) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 pb-4 sm:pb-0"
      onClick={onCerrar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="selector-vendedor-titulo"
        className="w-full max-w-sm bg-white rounded-3xl p-5 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="selector-vendedor-titulo" className="font-bold text-lg text-stone-900">
              ¿Con quién quieres hablar?
            </h2>
            <p className="text-sm text-stone-500 mt-0.5">
              Elige un asesor para completar tu pedido por WhatsApp.
            </p>
          </div>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-stone-500 hover:text-stone-800 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <ul className="space-y-2 max-h-[50vh] overflow-y-auto">
          {vendedores.map((v) => (
            <li key={v.codigo}>
              <button
                onClick={() => onElegir(v)}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors cursor-pointer ${
                  actual?.codigo === v.codigo
                    ? 'border-[#25d366] bg-[#25d366]/5'
                    : 'border-stone-200 hover:border-[#25d366]'
                }`}
              >
                <Avatar vendedor={v} />
                <span className="flex-1 font-semibold text-stone-800">{v.nombre}</span>
                <i className="ri-whatsapp-line text-xl text-[#25d366]" />
              </button>
            </li>
          ))}
        </ul>

        <button
          onClick={() => onElegir(null)}
          className="w-full h-11 rounded-xl text-sm font-medium text-stone-500 hover:text-stone-800 bg-stone-100 cursor-pointer"
        >
          Sin preferencia
        </button>
      </div>
    </div>
  );
}
